import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Globe,
  Network,
  Plug,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Search,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useLoginContext } from '../context/LoginContext';

// Styled Components
const Container = styled.div`
  padding: 1rem;
  max-width: 1600px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 1rem;
  }
  @media (min-width: 1280px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ScanCount = styled.span`
  font-size: 0.875rem;
  color: #00419F;
  font-weight: 500;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #1562B7;
    box-shadow: 0 0 0 1px #1562B7;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 1rem;
  height: 1rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  border: none;
  cursor: pointer;

  ${props =>
    props.variant === 'primary'
      ? `
    background: linear-gradient(90deg, #00419F 0%, #1562B7 50%, #1E90FF 100%);
    color: #FFFFFF;
    &:hover {
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
  `
      : `
    border: 1px solid #E5E7EB;
    color: #374151;
    background: white;
    &:hover {
      border-color: #D1D5DB;
    }
  `}

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #f3f4f6;
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const IconWrapper = styled.div`
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: rgba(30, 144, 255, 0.1);
  color: #00419F;

  @media (max-width: 640px) {
    padding: 0.5rem;
  }
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScanTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const VersionTag = styled.span`
  font-size: 0.75rem;
  color: #00419F;
  background-color: rgba(30, 144, 255, 0.1);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;

  @media (max-width: 640px) {
    font-size: 0.875rem;
  }
`;

const Target = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;

  @media (max-width: 640px) {
    white-space: normal;
  }
`;

const Details = styled.div`
  margin-bottom: 1.5rem;
  & > * + * {
    margin-top: 0.75rem;
  }

  @media (max-width: 640px) {
    margin-bottom: 1rem;
  }
`;

const TimeStamp = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #6b7280;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const VulnerabilitiesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: 0.5rem;
  }
`;

const ViewReportLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #00419F;
  background-color: rgba(30, 144, 255, 0.1);
  transition: background-color 0.2s;
  text-decoration: none;

  &:hover {
    background-color: rgba(30, 144, 255, 0.2);
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
    font-size: 1rem;
  }
`;

const StatusWrapper = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem;
  border-radius: 9999px;
  
  ${props => {
    switch (props.status.toLowerCase()) {
      case 'completed':
        return 'color: #10b981; background-color: #ecfdf5;';
      case 'in-progress':
        return 'color: #3b82f6; background-color: #eff6ff;';
      case 'terminated':
        return 'color: #ef4444; background-color: #fef2f2;';
      default:
        return 'color: #9ca3af; background-color: #f3f4f6;';
    }
  }}
`;

const SeverityDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  
  ${props => {
    switch (props.type) {
      case 'Critical':
        return 'background-color: #ef4444;';
      case 'High':
        return 'background-color: #f87171;';
      case 'Medium':
        return 'background-color: #fbbf24;';
      case 'Low':
        return 'background-color: #34d399;';
      case 'Info':
        return 'background-color: #3c99f4;';
      default:
        return 'background-color: #9ca3af;';
    }
  }}

  @media (max-width: 640px) {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const SeverityCount = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #4b5563;

  @media (max-width: 640px) {
    font-size: 0.875rem;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 2px solid #E5E7EB;
  border-top-color: #1562B7;
  animation: ${spin} 1s linear infinite;

  @media (max-width: 640px) {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #dc2626;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #64748b;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #94a3b8;
`;

const SeverityIndicator = ({ count = 0, type }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <SeverityDot type={type} />
      <SeverityCount>{count}</SeverityCount>
    </div>
  );
};

const ScanTypeIcon = ({ type }) => {
  const icons = {
    website: Globe,
    network: Network,
    api: Plug,
    firewall: Shield
  };
  const Icon = icons[type] || Globe;
  return <Icon size={24} />;
};

const StatusIndicator = ({ status }) => {
  if (!status) return null;

  const statusConfig = {
    completed: CheckCircle,
    'in-progress': Clock,
    terminated: AlertTriangle
  };

  const key = status.toLowerCase().replace(' ', '-');
  const Icon = statusConfig[key] || Clock;

  return (
    <StatusWrapper status={status}>
      <Icon size={16} />
    </StatusWrapper>
  );
};

function AutomatedReportPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useLoginContext();
  const userTimeZone = user?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const fetchScanHistory = async () => {
      try {
        const response = await apiRequest('/api/scanhistory', { method: 'GET' });
        if (!response.ok) throw new Error('Failed to fetch scan history');
        const data = await response.json();
        setScans(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchScanHistory();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimeZone,
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(dateString));
  };

  const filteredScans = scans.filter(scan =>
    scan.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (scan.asset?.name && scan.asset.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container>
          <LoadingWrapper>
            <LoadingSpinner />
          </LoadingWrapper>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container>
          <ErrorWrapper>
            <AlertTriangle size={20} style={{ marginRight: '0.5rem' }} />
            Error: {error}
          </ErrorWrapper>
        </Container>
      </div>
    );
  }

  const historyData = [...filteredScans].sort((a, b) => new Date(b.time) - new Date(a.time));

  return (
    <div className="min-h-screen bg-gray-50">
      <Container>
        {/* <Header>
          <HeaderTop>
            <Title>Automated Scans</Title>
            <ScanCount>{scans.length} Total Scans</ScanCount>
          </HeaderTop>
          
          <SearchBar>
            <SearchWrapper>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search scans by target or asset name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>
          </SearchBar>
        </Header> */}

        {filteredScans.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <Activity size={24} />
            </EmptyStateIcon>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              No Automated Scans Found
            </h3>
            <p>No automated scans match your search criteria.</p>
          </EmptyState>
        ) : (
          <CardsGrid>
            {historyData.map((scan, index) => (
              <Card key={index}>
                <StatusIndicator status={scan.status} />

                <CardHeader>
                  <IconWrapper>
                    <ScanTypeIcon type={scan.type} />
                  </IconWrapper>
                  <CardContent>
                    <ScanTitle>
                      {scan.asset?.name ? (
                        scan.asset.name
                      ) : (
                        `${scan.type.charAt(0).toUpperCase() + scan.type.slice(1)} Scan`
                      )}
                      <VersionTag>{scan?.version || 'v1.0'}</VersionTag>
                    </ScanTitle>
                    <Target title={scan.target}>
                      {scan.target}
                    </Target>
                  </CardContent>
                </CardHeader>

                <Details>
                  <TimeStamp>
                    <Clock size={16} style={{ marginRight: '0.5rem' }} />
                    {formatDate(scan.time)}
                  </TimeStamp>

                  {scan.vulnerabilityCounts && (
                    <VulnerabilitiesWrapper>
                      <SeverityIndicator count={scan.vulnerabilityCounts.Critical || 0} type="Critical" />
                      <SeverityIndicator count={scan.vulnerabilityCounts.High || 0} type="High" />
                      <SeverityIndicator count={scan.vulnerabilityCounts.Medium || 0} type="Medium" />
                      <SeverityIndicator count={scan.vulnerabilityCounts.Low || 0} type="Low" />
                      <SeverityIndicator count={scan.vulnerabilityCounts.Info || 0} type="Info" />
                    </VulnerabilitiesWrapper>
                  )}
                </Details>

                <ViewReportLink to={scan.reportLink}>
                  <ExternalLink size={16} style={{ marginRight: '0.5rem' }} />
                  View Report
                </ViewReportLink>
              </Card>
            ))}
          </CardsGrid>
        )}
      </Container>
    </div>
  );
}

export default AutomatedReportPage;